import React, { useEffect, useState, useCallback } from "react";
import { Button, Col, Row, Alert, Spinner } from "reactstrap";
import PropTypes from "prop-types";
import XplorPaySDK from "./XplorPaySDK";
import XPLORPAY_CONFIG from "./XplorPayConfig";
import { processXplorPayCardPayment } from "api/PaymentsAPI";
import { failAlert, successAlertCustom } from "utils/alertUtils";
import { useFromUser } from "stores/LocalStorageHelper";
import useSetAccount from "account/useSetAccount";
import useSetDeal from "features/deal/hooks/useSetDeal";

/**
 * XplorPay Credit/Debit Card Payment Component
 *
 * Renders XplorPay's hosted payment iframe and handles payment processing.
 * Card data is collected by XplorPay's iframe - never touches our code.
 *
 * @param {number} pmtAmount - Amount to charge
 * @param {number} accountID - Account ID (optional, for account payments)
 * @param {number} dealID - Deal ID (optional, for down payments)
 * @param {Object} pmtObj - Payment object to update with results
 * @param {Function} setPmtObj - Function to update payment object
 * @param {Function} addPayment - Callback when payment completes
 * @param {Function} onSuccess - Optional success callback
 * @param {Function} onError - Optional error callback
 * @param {Function} toggle - Optional function to close modal
 */
const XplorPayPayment = ({
  pmtAmount,
  accountID = null,
  dealID = null,
  pmtObj,
  setPmtObj,
  addPayment,
  onSuccess,
  onError,
  toggle,
}) => {
  const dealerID = useFromUser("dealerID");
  const locationID = useFromUser("locationID");

  const [loading, setLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState(null);

  const setAccount = useSetAccount();
  const setDeal = useSetDeal();

  // Initialize SDK on mount
  useEffect(() => {
    let mounted = true;

    const initializeSDK = async () => {
      try {
        setError(null);
        await XplorPaySDK.initForCard();
        if (mounted) {
          setSdkReady(true);
        }
      } catch (err) {
        console.error("Failed to initialize XplorPay SDK:", err);
        if (mounted) {
          setError(err.message || "Failed to load payment form");
        }
      }
    };

    initializeSDK();

    // Cleanup on unmount
    return () => {
      mounted = false;
      XplorPaySDK.cleanup();
    };
  }, []);

  // Handle payment processing
  const handleProcessPayment = useCallback(async () => {
    if (!pmtAmount || pmtAmount <= 0) {
      setError("Please enter a valid payment amount");
      return;
    }

    if (pmtAmount < 1) {
      setError("Minimum payment amount is $1.00");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Step 1: Get JWT token from XplorPay SDK
      const tokenResponse = await XplorPaySDK.getPaymentToken();
      const { jwt, lastFour, cardType } = tokenResponse;

      if (!jwt) {
        throw new Error("Failed to obtain payment token");
      }

      // Step 2: Send JWT to our backend for processing
      const paymentData = {
        jwt,
        amount: pmtAmount,
        accountId: accountID,
        dealId: dealID,
        dealerId: dealerID,
        locationId: locationID,
        lastFour,
        cardType,
        paymentMethod: "CARD",
      };

      const response = await processXplorPayCardPayment(paymentData);

      if (response.data && response.data.content && response.data.content.success) {
        const paymentResult = response.data.content;

        // Update payment object with transaction details
        const updatedPmtObj = {
          ...pmtObj,
          processor: "xplorpay",
          transactionId: paymentResult.transactionId,
          authCode: paymentResult.authCode,
          last4: lastFour,
          cardType: cardType,
          status: "completed",
        };

        if (setPmtObj) {
          setPmtObj(updatedPmtObj);
        }

        // Handle account or deal update from response
        if (accountID !== null && response.data.content.account) {
          setAccount(response.data.content.account);
        } else if (dealID !== null && response.data.content.deal) {
          setDeal(response.data.content.deal);
        }

        // Call success callback
        if (addPayment) {
          addPayment("completed");
        }

        if (onSuccess) {
          onSuccess(paymentResult);
        }

        successAlertCustom("Payment processed successfully!");

        // Close modal if provided
        if (toggle) {
          toggle();
        }
      } else {
        throw new Error(
          response.data?.content?.displayMessage ||
          response.data?.content?.errorMessage ||
          response.data?.message ||
          "Payment failed"
        );
      }
    } catch (err) {
      console.error("Payment processing error:", err);
      const errorMessage = err.response?.data?.message || err.message || "Payment processing failed. Please try again.";
      setError(errorMessage);
      failAlert(errorMessage);

      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [pmtAmount, accountID, dealID, dealerID, locationID, pmtObj, setPmtObj, addPayment, onSuccess, onError, toggle, setAccount, setDeal]);

  return (
    <div className="xplorpay-payment-container">
      <Row className="mb-3">
        <Col sm="12">
          <h5>Credit/Debit Card Payment</h5>
          <p className="text-muted mb-2">
            Amount: <strong>${pmtAmount?.toFixed(2) || "0.00"}</strong>
          </p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col sm="12">
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {!sdkReady && !error && (
        <Row className="mb-3">
          <Col sm="12" className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading secure payment form...</p>
          </Col>
        </Row>
      )}

      {/* XplorPay hosted payment form renders here */}
      <Row className="mb-3">
        <Col sm="12">
          <div
            id={XPLORPAY_CONFIG.paymentFormId}
            style={{
              minHeight: sdkReady ? "200px" : "0",
              border: sdkReady ? "1px solid #ddd" : "none",
              borderRadius: "4px",
              padding: sdkReady ? "15px" : "0",
              backgroundColor: "#f9f9f9",
            }}
          />
        </Col>
      </Row>

      {sdkReady && (
        <Row>
          <Col sm="12" className="text-center">
            <Button
              color="primary"
              size="lg"
              onClick={handleProcessPayment}
              disabled={loading || !pmtAmount || pmtAmount <= 0}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" /> Processing...
                </>
              ) : (
                `Pay $${pmtAmount?.toFixed(2) || "0.00"}`
              )}
            </Button>
          </Col>
        </Row>
      )}

      <Row className="mt-3">
        <Col sm="12" className="text-center">
          <small className="text-muted">
            <i className="fa fa-lock mr-1"></i>
            Secured by XplorPay. Card data is encrypted and never stored on our servers.
          </small>
        </Col>
      </Row>
    </div>
  );
};

XplorPayPayment.propTypes = {
  pmtAmount: PropTypes.number.isRequired,
  accountID: PropTypes.number,
  dealID: PropTypes.number,
  pmtObj: PropTypes.object,
  setPmtObj: PropTypes.func,
  addPayment: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  toggle: PropTypes.func,
};

export default XplorPayPayment;
